import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { get, put } from '@/lib/http';

/** One cell of the plan: a sub-line's target for a calendar month. */
export interface TargetRow {
  subline: string;
  month: string;
  units: number;
  gmv: number;
}

export interface TargetVersion {
  version: number;
  created_at: string;
  created_by: string;
  note: string;
}

export interface TargetsResponse {
  /** null until a plan has been saved for this brand — not an error. */
  version: TargetVersion | null;
  versions: TargetVersion[];
  rows: TargetRow[];
}

const KEY = (customer: string): unknown[] => ['targets', customer];

export function useTargets(customer: string): UseQueryResult<TargetsResponse, Error> {
  return useQuery({
    queryKey: KEY(customer),
    enabled: customer !== '',
    // Targets change when a person edits them, not on a clock. Refetching on
    // an interval would fight an open editor.
    staleTime: Infinity,
    queryFn: ({ signal }) =>
      get<TargetsResponse>(
        `/v1/reports/ops/targets?customer_name=${encodeURIComponent(customer)}`,
        signal,
      ),
  });
}

/**
 * Saving creates a NEW version rather than overwriting the current one, so the
 * plan a past report was measured against stays recoverable. The server owns
 * that rule; this only has to invalidate the right caches afterwards.
 *
 * Plan-vs-actual is invalidated too: it reads the active version, so leaving
 * it cached would show the previous plan against the same actuals until the
 * page was reloaded.
 */
export function useSaveTargets(customer: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { rows: TargetRow[]; note: string }) =>
      put<TargetsResponse>('/v1/reports/ops/targets', { customer_name: customer, ...body }),
    onSuccess: (data) => {
      qc.setQueryData(KEY(customer), data);
      void qc.invalidateQueries({ queryKey: ['/v1/reports/ops/plan-actual'] });
    },
  });
}
